import { CheckIcon, UpdateIcon } from '@radix-ui/react-icons'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import ArrowDown from '@/components/ui/icons/arrow-down'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { groupMedicationOptions, type MedicationOption } from '@/lib/medication-options'
import { cn } from '@/lib/utils'
import { useMemo, useState } from 'react'

type Row = { type: 'header'; title: string } | { type: 'option'; option: MedicationOption }

const HEADER_ROW_HEIGHT = 34
const OPTION_ROW_HEIGHT = 42

export const OutOfStockTag = () => (
  <span className='shrink-0 rounded-full bg-[#FFE5E5] px-2 py-0.5 text-[11px] leading-4 font-medium text-red-700'>
    Out of stock
  </span>
)

export default function MedicationCombobox({
  options,
  title,
  onValueChange,
  defaultValue: value,
  isDisabled = false,
  disabledValues
}: {
  options: MedicationOption[]
  title: string
  onValueChange: (value: string) => void
  defaultValue?: string
  isDisabled?: boolean
  /** Option values rendered as disabled rows — visible but not selectable. */
  disabledValues?: Set<string>
}) {
  const [open, setOpen] = useState(false)
  const [parentNode, setParentNode] = useState<HTMLDivElement | null>(null)

  const [searchQuery, setSearchQuery] = useState('')

  const rows = useMemo<Row[]>(() => {
    // every whitespace-separated token must match somewhere, so "tadalafil 28" finds "Tadalafil - 5mg, 28 Tablets"
    const tokens = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean)
    const visible = tokens.length
      ? options.filter(option => tokens.every(token => option.searchText.includes(token)))
      : options

    return groupMedicationOptions(visible).flatMap<Row>(group => [
      { type: 'header', title: group.title },
      ...group.options.map(option => ({ type: 'option' as const, option }))
    ])
  }, [searchQuery, options])

  const virtualizerProps = useMemo(
    () => ({
      count: rows.length,
      getScrollElement: () => parentNode,
      estimateSize: (index: number) => (rows[index]?.type === 'header' ? HEADER_ROW_HEIGHT : OPTION_ROW_HEIGHT),
      // measurements are cached per key — index keys go stale when filtering reshuffles the rows
      getItemKey: (index: number) => {
        const row = rows[index]
        if (!row) return index
        return row.type === 'header' ? `header:${row.title}` : row.option.value
      },
      overscan: 8
    }),
    [rows, parentNode]
  )
  const rowVirtualizer = useVirtualizer(virtualizerProps)

  const onSearchChange = (query: string) => {
    setSearchQuery(query)

    // Delegate the scroll to the virtualizer to avoid race conditions.
    setTimeout(() => {
      rowVirtualizer.scrollToOffset(0)
    })
  }

  const selectedOption = value ? options.find(option => option.value === value) : undefined

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          disabled={isDisabled}
          type='button'
          role='combobox'
          aria-expanded={open}
          className={cn(
            'py-4 px-3 rounded-2xl border border-custom-neutral-200 bg-transparent flex gap-4 items-center justify-between transition-all duration-200 ease-in-out hover:bg-secondary/20 w-full relative',
            isDisabled && 'opacity-50 cursor-not-allowed hover:bg-transparent'
          )}
        >
          <div className='absolute left-4 -top-2 text-xs leading-3 tracking-[0.15px] text-custom-neutral-800 p-1 bg-white rounded-lg'>
            {title}
          </div>

          <span className='text-base font-normal leading-6 tracking-[0.15px] truncate text-left'>
            {selectedOption?.label ?? 'Select an option...'}
          </span>

          {selectedOption && !selectedOption.isInStock && <OutOfStockTag />}

          <ArrowDown className='ml-auto shrink-0' />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='w-(--radix-popover-trigger-width) p-0 rounded-xl max-h-full overflow-hidden [&_div]:overflow-hidden'
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder='Search by name or strength...'
            className='h-10'
            value={searchQuery}
            onValueChange={onSearchChange}
          />
          <CommandList className='max-h-none'>
            <CommandEmpty>No option found.</CommandEmpty>
            <CommandGroup className='p-0'>
              {/* 3rem ≈ the search input above the list; keeps the popover inside the viewport on short screens */}
              <div
                ref={setParentNode}
                className='overflow-auto! w-full h-max max-h-[min(400px,calc(var(--radix-popover-content-available-height,400px)-3rem))]'
              >
                <div
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative'
                  }}
                >
                  {rowVirtualizer.getVirtualItems().map(virtualRow => {
                    const row = rows[virtualRow.index]
                    if (!row) return null

                    if (row.type === 'header') {
                      return (
                        <div
                          key={`header-${row.title}-${virtualRow.index}`}
                          ref={rowVirtualizer.measureElement}
                          data-index={virtualRow.index}
                          className='absolute left-0 top-0 w-full bg-[#f7f8fc] px-4 py-2 flex items-center gap-2'
                          style={{
                            transform: `translateY(${virtualRow.start}px)`
                          }}
                        >
                          <span className='truncate text-[13px] leading-4.5 font-semibold tracking-[0.04em] text-custom-indigo-600'>
                            {row.title}
                          </span>
                        </div>
                      )
                    }

                    const { option } = row
                    const isOptionDisabled = disabledValues?.has(option.value) ?? false
                    return (
                      <CommandItem
                        key={`${option.value}-${virtualRow.index}`}
                        ref={rowVirtualizer.measureElement}
                        value={option.value}
                        disabled={isOptionDisabled}
                        onSelect={() => {
                          if (isOptionDisabled) return
                          onValueChange(option.value)
                          setOpen(false)
                        }}
                        data-index={virtualRow.index}
                        className='absolute left-0 top-0 w-full gap-3 px-4 py-2.5'
                        style={{
                          transform: `translateY(${virtualRow.start}px)`
                        }}
                      >
                        {option.strength && (
                          <span className='shrink-0 rounded-full bg-custom-blue-100 px-2.5 py-0.5 text-[13px] leading-4.5 font-medium text-custom-blue-600'>
                            {option.strength}
                          </span>
                        )}
                        <span className='truncate text-sm text-custom-neutral-900'>{option.detail}</span>
                        <span className='ml-auto flex shrink-0 items-center gap-2 text-[13px] text-custom-neutral-400'>
                          {!option.isInStock && <OutOfStockTag />}
                          {option.supply}
                          {option.frequency && (
                            <span className='flex shrink-0 items-center gap-1 rounded-full bg-custom-blue-100 px-2 py-0.5 text-[11px] leading-4 font-medium text-custom-blue-600'>
                              <UpdateIcon className='h-3 w-3 shrink-0' />
                              {option.frequency}
                            </span>
                          )}
                          <CheckIcon
                            className={cn('h-4 w-4 shrink-0', value === option.value ? 'opacity-100' : 'opacity-0')}
                          />
                        </span>
                      </CommandItem>
                    )
                  })}
                </div>
              </div>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
